<template>
  <section
    class="tw-flex tw-h-screen tw-items-center tw-justify-center tw-text-center"
  >
    <div v-if="showError" class="tw-flex tw-m-auto tw-flex-col">
      <p>
        {{ $t("m365-connection-error") }}
      </p>

      <div class="tw-flex tw-cursor-pointer tw-relative">
        <div class="tw-flex tw-z-10 tw-items-center">
          <a
            class="tw-text-primary-grey-700"
            @click="errorInDetail = !errorInDetail"
          >
            {{ $t("m365-show-error") }}
          </a>
          <cb-icon-chevron-down
            :width="16"
            class="tw-transition-transform tw-text-primary-grey-700"
            :class="errorInDetail ? 'tw-rotate-180' : 'tw-rotate-0'"
          />
        </div>
        <div
          v-if="errorInDetail"
          class="tw-absolute tw-top-0 tw-z-0 tw-flex tw-flex-col"
        >
          <p v-if="error">
            <span class="tw-font-bold">Error: </span> {{ error }}
          </p>
          <p v-if="errorDescription">
            <span class="tw-font-bold">Error Description: </span
            >{{ errorDescription }}
          </p>
          <p v-if="admin_consent">
            ><span class="tw-font-bold">Admin Consent: </span
            >{{ admin_consent }}
          </p>
          <p v-if="state">
            ><span class="tw-font-bold">State: </span>{{ state }}
          </p>
        </div>
      </div>
    </div>
    <div v-else>
      {{ $t("m365-account-connected") }}
      <p>
        {{ $t("page-close-countdown") }}
        <span id="countdowntimer">5 </span>
        {{ $t("page-close-seconds") }}
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useAzureStore } from "@/store/azure.store";
import { useMicrosoftConnectStore } from "@/store/microsoftConnect.store";
import { persistConsentResult } from "@/utils/consent";

type RouteQuery = {
  success?: string;
  orgID?: string;
  error?: string;
  errorDescription?: string;
  admin_consent?: string;
  state?: string;
};

const showError = ref<boolean>(false);
const errorInDetail = ref<boolean>(true);
const errorDescription = ref<string>("");
const error = ref<string>("");
const admin_consent = ref<string>("");
const state = ref<string>("");

const route = useRoute();
const azureStore = useAzureStore();
const microsoftConnectStore = useMicrosoftConnectStore();

const closeWindow = (): void => {
  window.close();
};

const startCountdown = (): void => {
  let timeleft = 5;
  const downloadTimer = setInterval(() => {
    timeleft--;

    const countdownEl = document.getElementById("countdowntimer");
    if (countdownEl) {
      countdownEl.textContent = timeleft.toString();
    }

    if (timeleft <= 0) {
      clearInterval(downloadTimer);
      window.close();
    }
  }, 1000);
};

onMounted(() => {
  const query = route.query as RouteQuery;
  const isQuerySuccess = query.success === "true";
  const isQueryFalse = query.success === "false";
  const azureConnection = new BroadcastChannel("azure-connection");

  if (isQuerySuccess) {
    // Persist the outcome from the redirect URL before the popup auto-closes.
    // The opener reads this to decide whether the connect/update actually
    // succeeded — closing the popup leaves no result, so success can never be
    // inferred from the connection state alone. The BroadcastChannel message
    // (which can be lost in the auto-close race) is only a best-effort signal.
    persistConsentResult({ success: true });
    azureStore.getAllAzureAccounts(query.orgID);
    microsoftConnectStore.connectToMicroSoft(true);
    azureConnection.postMessage({ data: query.orgID });
  } else if (isQueryFalse) {
    persistConsentResult({
      success: false,
      error: query.error || "",
      errorDescription: query.errorDescription || "",
    });
    azureConnection.postMessage({ data: null });

    microsoftConnectStore.connectToMicroSoft(query);
    showError.value = true;
    error.value = query.error || "";
    errorDescription.value = query.errorDescription || "";
    admin_consent.value = query.admin_consent || "";
    state.value = query.state || "";
  }

  if (query.success) {
    startCountdown();
  }
});

defineExpose({
  closeWindow,
});
</script>
